import { Keys } from "../functions/Keys"

type TupleFirst<T extends unknown[]> = T extends [infer F, ...unknown[]]
  ? F
  : never
type TupleRest<T extends unknown[]> = T extends [unknown, ...infer R] ? R : []

export function MethodDispatcher<
  Args extends [Record<string, unknown>, ...unknown[]],
  Return,
  DiscriminantKey extends keyof TupleFirst<Args> = "enum",
  UnionType extends TupleFirst<Args> = TupleFirst<Args>,
  DiscriminantValues extends
    UnionType[DiscriminantKey] = UnionType[DiscriminantKey],
>(
  handlers: Partial<{
    [V in DiscriminantValues & (number | string)]: (
      item: Extract<UnionType, Record<DiscriminantKey, V>>,
      ...restArgs: TupleRest<Args>
    ) => Return
  }>,
  fallback: (item: UnionType, ...restArgs: TupleRest<Args>) => Return = (
    item,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ..._restArgs
  ) => {
    const allowedValues = Keys(handlers)
      .map(s => `${s}`)
      .join(", ")
    throw new Error(
      `Unknown ${String(discriminant)}: "${String(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item as any)[discriminant]
      )}". Expected one of: ${allowedValues}.`
    )
  },
  discriminant: DiscriminantKey = "enum" as DiscriminantKey
) {
  return (item: UnionType, ...restArgs: TupleRest<Args>): Return => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const discriminator = (item as any)[discriminant] as DiscriminantValues &
      (number | string)
    const handler = handlers[discriminator]
    if (handler) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return handler(item as any, ...restArgs)
    }
    return fallback(item, ...restArgs)
  }
}

export function FullMethodDispatcher<
  Args extends [Record<string, unknown>, ...unknown[]],
  Return,
  DiscriminantKey extends keyof TupleFirst<Args> = "enum",
  UnionType extends TupleFirst<Args> = TupleFirst<Args>,
  DiscriminantValues extends
    UnionType[DiscriminantKey] = UnionType[DiscriminantKey],
>(handlers: {
  [V in DiscriminantValues & (number | string)]: (
    item: Extract<UnionType, Record<DiscriminantKey, V>>,
    ...restArgs: TupleRest<Args>
  ) => Return
}) {
  // ✅ Explicitly forward all type parameters
  return MethodDispatcher<
    Args,
    Return,
    DiscriminantKey,
    UnionType,
    DiscriminantValues
  >(handlers)
}
